import type { components } from '@connected/api-client/api.generated';

import { api } from '@connected/api-client';
import { elektraAppQueryOptions } from '@connected/elektra-sdk/queries/query-options';

import { queryClient } from '@/lib/query-client';

export type UseCreateDistributionBoardOptions = {
    electricalProjectVersionId: string;
    onSuccess?: (distributionBoardId: string) => void;
};

export function useCreateDistributionBoardMutation({
    electricalProjectVersionId,
    onSuccess,
}: UseCreateDistributionBoardOptions) {
    return api.useMutation(
        'post',
        '/api/v2/electrical/projects/distributionboards/create',
        {
            onMutate: async (variable) => {
                const distributionBoardQueryOptions = elektraAppQueryOptions.project.inspection.distributionBoard.distributionBoardOptions({
                    electricalProjectDistributionBoardId: variable!.body!.ElectricalProjectDistributionBoardId!,
                });

                const distributionBoardQueryListOptions = elektraAppQueryOptions.project.inspection.distributionBoard.distributionBoardListOptions({
                    electricalProjectVersionId,
                });

                const previousDataList = queryClient.getQueryData(distributionBoardQueryListOptions.queryKey) as components['schemas']['ListElectricalProjectDistributionBoardQueryResult'];

                const summaryOptions = elektraAppQueryOptions.project.recapViewOptions({
                    electricalProjectVersionId,
                });

                await queryClient.cancelQueries(distributionBoardQueryOptions);
                await queryClient.cancelQueries(distributionBoardQueryListOptions);
                await queryClient.cancelQueries(summaryOptions);

                queryClient.setQueryData<components['schemas']['ListElectricalProjectDistributionBoardQueryResult']>(distributionBoardQueryListOptions.queryKey, (old) => {
                    if (old && variable) {
                        const updatedData: components['schemas']['ListElectricalProjectDistributionBoardQueryResult'] = {
                            ...old,
                            DistributionBoards: [
                                ...(old.DistributionBoards || []),
                                {
                                    ElectricalProjectDistributionBoardId: variable?.body?.ElectricalProjectDistributionBoardId,
                                    Identifier: variable?.body?.Identifier,
                                    Kind: variable?.body?.Kind,
                                    FloorNumber: null,
                                    SpaceNumber: null,
                                    Order: 9999,
                                },
                            ],
                        };
                        return updatedData;
                    }
                    return old;
                });

                const previousDataSingle = queryClient.getQueryData(distributionBoardQueryListOptions.queryKey) as components['schemas']['EditElectricalProjectDistributionBoardQueryResult'];
                queryClient.setQueryData<components['schemas']['EditElectricalProjectDistributionBoardQueryResult']>(
                    distributionBoardQueryOptions.queryKey,
                    (old) => {
                        const updatedData: components['schemas']['EditElectricalProjectDistributionBoardQueryResult'] = {
                            ...old,
                            DistributionBoard: {
                                ElectricalProjectDistributionBoardId: variable!.body!.ElectricalProjectDistributionBoardId!,
                                Identifier: variable?.body?.Identifier,
                                Kind: variable?.body?.Kind,

                            },
                        };
                        return updatedData;
                    },

                );

                queryClient.setQueryData<components['schemas']['ViewElectricalProjectSummaryQueryResult']>(
                    summaryOptions.queryKey,
                    (old) => {
                        if (old && old.Summary) {
                            return {
                                ...old,
                                Summary: {
                                    ...old.Summary,
                                    ElectricalProjectVersionId: old.Summary.ElectricalProjectVersionId,
                                    DistributionBoardCount: (old.Summary.DistributionBoardCount || 0) + 1,
                                },
                            };
                        }
                        return old;
                    },
                );

                onSuccess?.(variable!.body!.ElectricalProjectDistributionBoardId!);

                return { previousDataList, previousDataSingle };
            },
            onSuccess: async (data) => {
                queryClient.invalidateQueries(
                    elektraAppQueryOptions.project.inspection.distributionBoard.distributionBoardListOptions({ electricalProjectVersionId }),
                );

                queryClient.invalidateQueries(
                    elektraAppQueryOptions.project.inspection.distributionBoard.distributionBoardOptions({
                        electricalProjectDistributionBoardId: data.ElectricalProjectDistributionBoardId,
                    }),
                );
            },
            onError: (error, _variable, context: any) => {
                console.error(error);

                queryClient.invalidateQueries(elektraAppQueryOptions.project.inspection.distributionBoard.distributionBoardListOptions({ electricalProjectVersionId }));

                if (context?.previousDataList) {
                    queryClient.setQueryData(
                        elektraAppQueryOptions.project.inspection.distributionBoard.distributionBoardListOptions({ electricalProjectVersionId }).queryKey,
                        context.previousDataList,
                    );
                }

                if (context?.previousDataSingle) {
                    queryClient.setQueryData(
                        elektraAppQueryOptions.project.inspection.distributionBoard.distributionBoardOptions({
                            electricalProjectDistributionBoardId: context.previousDataSingle.DistributionBoard.ElectricalProjectDistributionBoardId!,
                        }).queryKey,
                        context.previousDataSingle,
                    );
                }
            },
            scope: {
                id: electricalProjectVersionId,
            },
        },
    );
}

export type UseCreateChecklistItemOptions = {
    electricalProjectVersionId: string;
    checklistItems: { ElectricalCheckListItemId: string; Description: string }[];
};

export function useCreateChecklistItemMutation({
    electricalProjectVersionId,
    checklistItems,
}: UseCreateChecklistItemOptions) {
    return api.useMutation('post', '/api/v2/electrical/projects/checklists/items/create', {
        onMutate: async (variable) => {
            const checkListItemsQueryOptions = elektraAppQueryOptions.project.inspection.distributionBoard.checkListItemsOptions({
                electricalProjectVersionId: variable!.body!.ElectricalProjectVersionId!,
                electricalProjectDistributionBoardId: variable!.body!.ObjectId!,
            });

            const previousDataList = queryClient.getQueryData(checkListItemsQueryOptions.queryKey) as components['schemas']['ListElectricalProjectCheckListItemQueryResult'];

            await queryClient.cancelQueries(checkListItemsQueryOptions);

            queryClient.setQueryData<components['schemas']['ListElectricalProjectCheckListItemQueryResult']>(
                elektraAppQueryOptions.project.inspection.distributionBoard.checkListItemsOptions({
                    electricalProjectVersionId: variable!.body!.ElectricalProjectVersionId!,
                    electricalProjectDistributionBoardId: variable!.body!.ObjectId!,
                }).queryKey,
                (old) => {
                    if (old) {
                        const updatedData: components['schemas']['ListElectricalProjectCheckListItemQueryResult'] = {
                            ...old,
                            Items: [
                                ...(old.Items || []),
                                {
                                    ConclusionKind: null,
                                    Description: checklistItems.find(item => item.ElectricalCheckListItemId === variable!.body!.ElectricalCheckListItemId)?.Description,
                                    ElectricalCheckListItemId: variable!.body!.ElectricalCheckListItemId,
                                    ObjectKind: variable!.body!.ObjectKind,
                                    ElectricalProjectDistributionBoardId: variable!.body!.ObjectId,
                                    ElectricalProjectCheckListItemId: variable!.body!.ElectricalProjectCheckListItemId,
                                },
                            ],
                        };

                        return updatedData;
                    }
                    else {
                        const updatedData: components['schemas']['ListElectricalProjectCheckListItemQueryResult'] = {
                            Items: [
                                {
                                    ConclusionKind: null,
                                    Description: checklistItems.find(item => item.ElectricalCheckListItemId === variable!.body!.ElectricalCheckListItemId)?.Description,
                                    ElectricalCheckListItemId: variable!.body!.ElectricalCheckListItemId,
                                    ObjectKind: variable!.body!.ObjectKind,
                                    ElectricalProjectDistributionBoardId: variable!.body!.ObjectId,
                                    ElectricalProjectCheckListItemId: variable!.body!.ElectricalProjectCheckListItemId,
                                },
                            ],
                        };

                        return updatedData;
                    }
                },
            );

            return { previousDataList };
        },
        onSuccess: async (variables) => {
            queryClient.invalidateQueries(
                elektraAppQueryOptions.project.inspection.distributionBoard.checkListItemsOptions({
                    electricalProjectVersionId,
                    electricalProjectDistributionBoardId: variables.ElectricalProjectCheckListItemId,
                }),
            );
        },
        onError: (error, _variable, context: any) => {
            console.error(error);

            queryClient.invalidateQueries(
                elektraAppQueryOptions.project.inspection.distributionBoard.checkListItemsOptions({
                    electricalProjectVersionId,
                    electricalProjectDistributionBoardId: _variable!.body!.ObjectId!,
                }),
            );

            if (context?.previousDataList) {
                queryClient.setQueryData(
                    elektraAppQueryOptions.project.inspection.distributionBoard.checkListItemsOptions({
                        electricalProjectVersionId,
                        electricalProjectDistributionBoardId: _variable!.body!.ObjectId!,
                    }).queryKey,
                    context.previousDataList,
                );
            }
        },
        scope: {
            id: electricalProjectVersionId,
        },
    });
}
